import React, { useState, useEffect, useCallback } from 'react';
import { authHeaders } from '../utils/authService';
import LoadingProgress from './LoadingProgress';

function fmtTime(sec) {
  if (!sec || sec <= 0) return '--:--.---';
  const min = Math.floor(sec / 60);
  const s = sec % 60;
  return min + ':' + s.toFixed(3).padStart(6, '0');
}

const S = {
  filterBar: { display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
  filterLabel: { fontFamily: 'var(--font-heading)', fontSize: 9, fontWeight: 500, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--color-text-dim)' },
  select: { padding: '8px 12px', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 6, color: 'var(--color-text)', fontFamily: 'var(--font-body)', fontSize: 13, outline: 'none', minWidth: 140 },
  input: { padding: '8px 12px', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 6, color: 'var(--color-text)', fontFamily: 'var(--font-body)', fontSize: 13, outline: 'none', width: 130 },
  btn: { padding: '8px 16px', border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-surface)', color: 'var(--color-text-dim)', fontFamily: 'var(--font-heading)', fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', cursor: 'pointer' },
  btnPrimary: { padding: '8px 16px', border: 'none', borderRadius: 6, background: 'var(--color-primary)', color: '#fff', fontFamily: 'var(--font-heading)', fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', cursor: 'pointer' },
  btnDanger: { padding: '4px 10px', border: '1px solid rgba(231,76,60,0.3)', borderRadius: 4, background: 'rgba(231,76,60,0.1)', color: '#e74c3c', fontSize: 11, cursor: 'pointer' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { fontFamily: 'var(--font-heading)', fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--color-text-dim)', padding: '12px 10px', background: 'var(--color-surface-light)', borderBottom: '1px solid var(--color-border)', textAlign: 'left', cursor: 'pointer' },
  td: { fontSize: 13, padding: '10px 10px', borderBottom: '1px solid var(--color-border)' },
  tdMono: { fontFamily: 'var(--font-data)', fontSize: 12 },
  wrap: { background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 10, overflow: 'hidden' },
  pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderTop: '1px solid var(--color-border)' },
  pageBtn: { padding: '5px 10px', border: '1px solid var(--color-border)', borderRadius: 4, background: 'var(--color-surface-light)', color: 'var(--color-text-dim)', fontSize: 12, cursor: 'pointer' },
  statsBar: { display: 'flex', gap: 24, marginBottom: 16, fontSize: 12, color: 'var(--color-text-dim)' },
};

const COLUMNS = [
  { key: 'id', label: 'ID', width: 60 },
  { key: 'session_id', label: 'Session ID', width: 160 },
  { key: 'display_name', label: 'Driver' },
  { key: 'track_name', label: 'Track' },
  { key: 'car', label: 'Car' },
  { key: 'car_class', label: 'Class', width: 70 },
  { key: 'lap_num', label: 'Lap #', width: 60 },
  { key: 'lap_time', label: 'Lap Time', width: 100 },
  { key: 'max_speed', label: 'Speed', width: 70 },
  { key: 'session_date', label: 'Date', width: 100 },
  { key: 'device_type', label: 'Source', width: 80 },
];

export default function LapDataBrowser() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [distinct, setDistinct] = useState({ drivers: [], tracks: [], cars: [], classes: [] });
  const [selected, setSelected] = useState(new Set());
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});

  // Filters
  const [filters, setFilters] = useState({
    display_name: '', track_id: '', car: '', car_class: '', device_type: '', session_id: '',
    date_from: '', date_to: '', sort: 'lap_time', order: 'asc', limit: 50, offset: 0,
  });

  const loadData = useCallback(async (f) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(f || filters).forEach(([k, v]) => { if (v !== '' && v !== null) params.set(k, v); });
      const res = await fetch(`/api/admin/laps?${params}`, { headers: authHeaders() });
      const data = await res.json();
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to load laps:', err);
    }
    setLoading(false);
  }, [filters]);

  const loadStats = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/laps/stats', { headers: authHeaders() });
      const data = await res.json();
      setStats(data);
      setDistinct(data.distinct || { drivers: [], tracks: [], cars: [], classes: [] });
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, []);

  useEffect(() => { loadData(); loadStats(); }, []); // eslint-disable-line

  const applyFilters = () => {
    const f = { ...filters, offset: 0 };
    setFilters(f);
    loadData(f);
    setSelected(new Set());
  };

  const clearFilters = () => {
    const f = { display_name: '', track_id: '', car: '', car_class: '', device_type: '', session_id: '', date_from: '', date_to: '', sort: 'lap_time', order: 'asc', limit: 50, offset: 0 };
    setFilters(f);
    loadData(f);
    setSelected(new Set());
  };

  const sortBy = (col) => {
    const newOrder = filters.sort === col && filters.order === 'asc' ? 'desc' : 'asc';
    const f = { ...filters, sort: col, order: newOrder, offset: 0 };
    setFilters(f);
    loadData(f);
  };

  const goPage = (dir) => {
    const f = { ...filters, offset: Math.max(0, filters.offset + dir * filters.limit) };
    if (f.offset >= total) return;
    setFilters(f);
    loadData(f);
    setSelected(new Set());
  };

  const deleteLap = async (id) => {
    if (!window.confirm(`Delete lap record #${id}?`)) return;
    await fetch(`/api/admin/laps/${id}`, { method: 'DELETE', headers: authHeaders() });
    loadData();
    loadStats();
  };

  const bulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} lap record(s)?`)) return;
    await fetch('/api/admin/laps/bulk', {
      method: 'DELETE', headers: authHeaders(),
      body: JSON.stringify({ ids: [...selected] }),
    });
    setSelected(new Set());
    loadData();
    loadStats();
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditValues({ display_name: row.display_name, car: row.car, car_class: row.car_class, lap_time: row.lap_time, max_speed: row.max_speed, session_type: row.session_type || '' });
  };

  const saveEdit = async () => {
    await fetch(`/api/admin/laps/${editingId}`, {
      method: 'PUT', headers: authHeaders(),
      body: JSON.stringify(editValues),
    });
    setEditingId(null);
    loadData();
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map(r => r.id)));
  };

  return (
    <div>
      {/* Stats bar */}
      {stats && (
        <div style={S.statsBar}>
          <span><strong style={{ color: 'var(--color-accent)' }}>{stats.total_laps}</strong> total laps</span>
          <span><strong>{stats.unique_drivers}</strong> drivers</span>
          <span><strong>{stats.unique_tracks}</strong> tracks</span>
          <span><strong>{stats.unique_cars}</strong> cars</span>
        </div>
      )}

      {/* Filter bar */}
      <div style={S.filterBar}>
        <div style={S.filterGroup}>
          <div style={S.filterLabel}>Driver</div>
          <select style={S.select} value={filters.display_name} onChange={e => setFilters(f => ({ ...f, display_name: e.target.value }))}>
            <option value="">All Drivers</option>
            {distinct.drivers.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div style={S.filterGroup}>
          <div style={S.filterLabel}>Track</div>
          <select style={S.select} value={filters.track_id} onChange={e => setFilters(f => ({ ...f, track_id: e.target.value }))}>
            <option value="">All Tracks</option>
            {distinct.tracks.map(t => <option key={t.track_id} value={t.track_id}>{t.track_name}</option>)}
          </select>
        </div>
        <div style={S.filterGroup}>
          <div style={S.filterLabel}>Car</div>
          <select style={S.select} value={filters.car} onChange={e => setFilters(f => ({ ...f, car: e.target.value }))}>
            <option value="">All Cars</option>
            {distinct.cars.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div style={S.filterGroup}>
          <div style={S.filterLabel}>Class</div>
          <select style={S.select} value={filters.car_class} onChange={e => setFilters(f => ({ ...f, car_class: e.target.value }))}>
            <option value="">All</option>
            {distinct.classes.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div style={S.filterGroup}>
          <div style={S.filterLabel}>Session</div>
          <select style={{ ...S.select, minWidth: 180, fontFamily: 'var(--font-data)', fontSize: 11 }} value={filters.session_id} onChange={e => setFilters(f => ({ ...f, session_id: e.target.value }))}>
            <option value="">All Sessions</option>
            {(distinct.sessions || []).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div style={S.filterGroup}>
          <div style={S.filterLabel}>From</div>
          <input type="date" style={S.input} value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} />
        </div>
        <div style={S.filterGroup}>
          <div style={S.filterLabel}>To</div>
          <input type="date" style={S.input} value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} />
        </div>
        <button style={S.btnPrimary} onClick={applyFilters}>Apply</button>
        <button style={S.btn} onClick={clearFilters}>Clear</button>
        {selected.size > 0 && (
          <button style={S.btnDanger} onClick={bulkDelete}>Delete {selected.size} selected</button>
        )}
      </div>

      {/* Table */}
      <div style={S.wrap}>
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={{ ...S.th, width: 40, cursor: 'default' }}>
                  <input type="checkbox" checked={selected.size === rows.length && rows.length > 0} onChange={toggleAll} />
                </th>
                {COLUMNS.map(col => (
                  <th key={col.key} style={{ ...S.th, width: col.width }} onClick={() => sortBy(col.key)}>
                    {col.label} {filters.sort === col.key ? (filters.order === 'asc' ? '\u25B2' : '\u25BC') : ''}
                  </th>
                ))}
                <th style={{ ...S.th, width: 100, cursor: 'default' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={COLUMNS.length + 2} style={S.td}><LoadingProgress label="Loading lap data" compact /></td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={COLUMNS.length + 2} style={{ ...S.td, textAlign: 'center', color: 'var(--color-text-dim)', padding: 32 }}>No lap records found</td></tr>
              )}
              {!loading && rows.map(row => {
                const isEditing = editingId === row.id;
                return (
                  <tr key={row.id} style={{ background: selected.has(row.id) ? 'rgba(46,134,193,0.06)' : undefined }}>
                    <td style={S.td}>
                      <input type="checkbox" checked={selected.has(row.id)} onChange={() => toggleSelect(row.id)} />
                    </td>
                    <td style={{ ...S.td, ...S.tdMono }}>{row.id}</td>
                    <td style={{ ...S.td, ...S.tdMono, fontSize: 11, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={row.session_id}>{row.session_id}</td>
                    <td style={S.td}>
                      {isEditing
                        ? <input style={{ ...S.input, width: 120 }} value={editValues.display_name} onChange={e => setEditValues(v => ({ ...v, display_name: e.target.value }))} />
                        : row.display_name || row.user_email}
                    </td>
                    <td style={S.td}>{row.track_name || row.track_id}</td>
                    <td style={S.td}>
                      {isEditing
                        ? <input style={{ ...S.input, width: 120 }} value={editValues.car} onChange={e => setEditValues(v => ({ ...v, car: e.target.value }))} />
                        : row.car}
                    </td>
                    <td style={S.td}>
                      {isEditing
                        ? <input style={{ ...S.input, width: 60 }} value={editValues.car_class} onChange={e => setEditValues(v => ({ ...v, car_class: e.target.value }))} />
                        : row.car_class}
                    </td>
                    <td style={{ ...S.td, ...S.tdMono }}>{row.lap_num}</td>
                    <td style={{ ...S.td, fontFamily: 'var(--font-data)', fontSize: 14, fontWeight: 500, color: 'var(--color-success)' }}>
                      {isEditing
                        ? <input type="number" step="0.001" style={{ ...S.input, width: 90 }} value={editValues.lap_time} onChange={e => setEditValues(v => ({ ...v, lap_time: parseFloat(e.target.value) }))} />
                        : fmtTime(row.lap_time)}
                    </td>
                    <td style={{ ...S.td, ...S.tdMono }}>{row.max_speed ? Math.round(row.max_speed) : '--'}</td>
                    <td style={{ ...S.td, fontSize: 12, color: 'var(--color-text-dim)' }}>{row.session_date}</td>
                    <td style={S.td}>
                      <span style={{ display: 'inline-block', padding: '2px 8px', background: 'rgba(46,134,193,0.08)', border: '1px solid rgba(46,134,193,0.2)', borderRadius: 4, fontFamily: 'var(--font-data)', fontSize: 10, color: 'var(--color-text-dim)', textTransform: 'uppercase' }}>{row.device_type}</span>
                    </td>
                    <td style={S.td}>
                      {isEditing ? (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button style={{ ...S.btnPrimary, padding: '4px 8px', fontSize: 10 }} onClick={saveEdit}>Save</button>
                          <button style={{ ...S.btn, padding: '4px 8px', fontSize: 10 }} onClick={() => setEditingId(null)}>Cancel</button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button style={{ ...S.btn, padding: '4px 8px', fontSize: 10 }} onClick={() => startEdit(row)}>Edit</button>
                          <button style={{ ...S.btnDanger, padding: '4px 8px' }} onClick={() => deleteLap(row.id)}>Del</button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={S.pagination}>
          <span style={{ fontSize: 12, color: 'var(--color-text-dim)' }}>
            Showing {filters.offset + 1}–{Math.min(filters.offset + filters.limit, total)} of {total}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={S.pageBtn} disabled={filters.offset === 0} onClick={() => goPage(-1)}>Prev</button>
            <button style={S.pageBtn} disabled={filters.offset + filters.limit >= total} onClick={() => goPage(1)}>Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}
